import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {Modal, IconButton, Alert} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import {FeatureRequest} from "../../../models/support";
import {newFeatureRequest} from "../../../components/business_logic/feature_request_requests";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";

interface NewFeatureRequestProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export default function NewFeatureRequest({open, onClose, onSuccess}: NewFeatureRequestProps) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [anonymous, setAnonymous] = useState(false);
    const [titleError, setTitleError] = useState(false);
    const [titleErrorMessage, setTitleErrorMessage] = useState('');
    const [descriptionError, setDescriptionError] = useState(false);
    const [descriptionErrorMessage, setDescriptionErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open) {
            resetForm();
        }
    }, [open]);

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setAnonymous(false);
        setTitleError(false);
        setTitleErrorMessage('');
        setDescriptionError(false);
        setDescriptionErrorMessage('');
        setSubmitSuccess(false);
        setSubmitError('');
    };

    const handleClose = () => {
        if (!isSubmitting) {
            resetForm();
            onClose();
        }
    };

    const validateInputs = () => {
        let isValid = true;

        if (!title || title.trim().length === 0) {
            setTitleError(true);
            setTitleErrorMessage('Title is required.');
            isValid = false;
        } else {
            setTitleError(false);
            setTitleErrorMessage('');
        }

        if (!description || description.trim().length === 0) {
            setDescriptionError(true);
            setDescriptionErrorMessage('Description is required.');
            isValid = false;
        } else if (description.trim().length < 10) {
            setDescriptionError(true);
            setDescriptionErrorMessage('Description must be at least 10 characters.');
            isValid = false;
        } else {
            setDescriptionError(false);
            setDescriptionErrorMessage('');
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateInputs()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitSuccess(false);
        setSubmitError('');

        try {
            const newRequest: FeatureRequest = {
                id: '',
                title: title.trim(),
                description: description.trim(),
                request_status: 'open',
                anonymous: anonymous,
            };

            const result = await newFeatureRequest(newRequest);
            
            if (result && Object.keys(result).length > 0) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    resetForm();
                    onClose();
                    if (onSuccess) {
                        onSuccess();
                    }
                }, 1500);
            } else {
                setSubmitError('Failed to create feature request. Please try again.');
            }
        } catch (error) {
            setSubmitError('An error occurred while creating the feature request.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal 
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && !isSubmitting) {
                    handleClose();
                }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Box 
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    position: 'relative',
                    width: "90%",
                    maxWidth: "600px",
                    maxHeight: "90vh",
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    outline: 'none',
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 4,
                }}
            >
                <IconButton
                    onClick={handleClose}
                    disabled={isSubmitting}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                        '&:disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Create Feature Request
                </Typography>

                {submitSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Feature request created successfully!
                    </Alert>
                )}

                {submitError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {submitError}
                    </Alert>
                )}

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="title">Title</FormLabel>
                    <TextField
                        error={titleError}
                        helperText={titleErrorMessage}
                        id="title"
                        type="text"
                        name="title"
                        placeholder="Brief description of your feature request"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        fullWidth
                        variant="outlined"
                        color={titleError ? 'error' : 'primary'}
                        disabled={isSubmitting}
                        autoFocus
                    />
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="description">Description</FormLabel>
                    <TextField
                        error={descriptionError}
                        helperText={descriptionErrorMessage}
                        id="description"
                        name="description"
                        placeholder="Please provide detailed information about the feature you'd like to see..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                        fullWidth
                        multiline
                        rows={6}
                        variant="outlined"
                        color={descriptionError ? 'error' : 'primary'}
                        disabled={isSubmitting}
                    />
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={anonymous}
                                onChange={(e) => setAnonymous(e.target.checked)}
                                disabled={isSubmitting}
                            />
                        }
                        label="Submit anonymously"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                        Your username will not be displayed with this feature request
                    </Typography>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />}
                    >
                        {isSubmitting ? 'Creating...' : 'Create Request'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
